import React, {memo, useEffect, useState} from 'react';
import Select, {components} from 'react-select';
import styled from 'styled-components';
import {
	BsSortNumericUp, BsSortAlphaUp, BsSortAlphaDown, BsSortNumericDown,
} from 'react-icons/bs';
import { IoSwapVertical } from 'react-icons/io5';
import {useDispatch, useSelector} from 'react-redux';
import {useCatalog} from './context/catalogContext';
import {useFilter} from './context/filterContext';
import {setCatalogProperty, resetCatalog} from '../../store/reducers/catalogSlice';
import caretDownIcon from '../../img/sprite/caret-down.svg';

const StyledSort = styled.div`
	display: flex;
	align-items: center;
	flex-shrink: 0;
`;

const StyledSelect = styled(Select)`
	& .react-select {
		&__indicators {
			display: none;
		}
		&__control {
			width: ${(props) => (props.$mobile ? 'auto' : '200px')};
			&:hover {
				border-color: #10db92;
			}
			&--is-focused {
				border-color: #10db92!important;
				box-shadow: 0 0 0  1px rgb(16,219, 146);
			}
			&--menu-is-open {
				box-shadow: 0 0 0  1px rgb(16,219, 146);
			}	
			&-icon {
				position: absolute;
				right: 10px;
			}	
		}
		
		&__option {
			border-bottom: 1px solid #E3E3E3;
			white-space: normal; /* Позволяет переносу текста */
			line-height: 1.4; /* Улучшает читаемость при переносе */
			
			&--is-selected {
				background: #F5F5F8;
				color: #000;
			}
			&:hover  {
				background: #F5F5F8;
				cursor: pointer;
			}
		}
	}
`;

const DefaultIcon = () => (
	<span className="default-sort-icon">
		<svg width="30" height="23" viewBox="0 0 30 23" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M21.75 11.75C21.75 11.9489 21.671 12.1397 21.5303 12.2803C21.3897 12.421 21.1989 12.5 21 12.5H9C8.80109 12.5 8.61032 12.421 8.46967 12.2803C8.32902 12.1397 8.25 11.9489 8.25 11.75C8.25 11.5511 8.32902 11.3603 8.46967 11.2197C8.61032 11.079 8.80109 11 9 11H21C21.1989 11 21.3897 11.079 21.5303 11.2197C21.671 11.3603 21.75 11.5511 21.75 11.75ZM24.75 6.5H5.25C5.05109 6.5 4.86032 6.57902 4.71967 6.71967C4.57902 6.86032 4.5 7.05109 4.5 7.25C4.5 7.44891 4.57902 7.63968 4.71967 7.78033C4.86032 7.92098 5.05109 8 5.25 8H24.75C24.9489 8 25.1397 7.92098 25.2803 7.78033C25.421 7.63968 25.5 7.44891 25.5 7.25C25.5 7.05109 25.421 6.86032 25.2803 6.71967C25.1397 6.57902 24.9489 6.5 24.75 6.5ZM17.25 15.5H12.75C12.5511 15.5 12.3603 15.579 12.2197 15.7197C12.079 15.8603 12 16.0511 12 16.25C12 16.4489 12.079 16.6397 12.2197 16.7803C12.3603 16.921 12.5511 17 12.75 17H17.25C17.4489 17 17.6397 16.921 17.7803 16.7803C17.921 16.6397 18 16.4489 18 16.25C18 16.0511 17.921 15.8603 17.7803 15.7197C17.6397 15.579 17.4489 15.5 17.25 15.5Z" fill="black"/>
		</svg>
	</span>
);

const SingleValue = ({children, ...props}) => {
	const {value, label} = props.data;
	return (
		<components.SingleValue {...props}>
			<span dangerouslySetInnerHTML={{__html: label}} />
		</components.SingleValue>
	);
};

const MobileControl = ({children, ...props}) => (
	<components.Control {...props}>
		{children}
		<span className="catalog__mob-sort-caret" aria-hidden="true">
			<img src={caretDownIcon} alt="" width={16} height={16} />
		</span>
	</components.Control>
);

const Control = ({children, ...props}) => {
	const {value} = props.getValue()[0] || {};
	return (
		<components.Control {...props}>
				<span className="react-select__control-icon">
					{value === '' && <DefaultIcon />}
					{value === 'date_asc' && <BsSortNumericDown size={20} style={{margin: '0 5px'}}/>}
					{value === 'date_desc' && <BsSortNumericUp size={20} style={{margin: '0 5px'}}/>}
					{value === 'name_asc' && <BsSortAlphaDown size={20} style={{margin: '0 5px'}}/>}
					{value === 'name_desc' && <BsSortAlphaUp size={20} style={{margin: '0 5px'}}/>}
				</span>
			{children}
		</components.Control>
	);
};

const Option = ({children, value, label, ...props}) => {
	return (
		<components.Option {...props}>
			<span className="react-select__control-icon">
				{value === '' && <DefaultIcon />}
				{value === 'date_asc' && <BsSortNumericDown size={20} style={{margin: '0 5px'}}/>}
				{value === 'date_desc' && <BsSortNumericUp size={20} style={{margin: '0 5px'}}/>}
				{value === 'name_asc' && <BsSortAlphaDown size={20} style={{margin: '0 5px'}}/>}
				{value === 'name_desc' && <BsSortAlphaUp size={20} style={{margin: '0 5px'}}/>}
			</span>
			<span dangerouslySetInnerHTML={{__html: label}} />
		</components.Option>
	);
};

const options = [
	{value: '', label: 'Сортировка'},
	{value: 'name_asc', label: 'По названию (А-Я)'},
	{value: 'name_desc', label: 'По названию (Я-А)'},
	{value: 'date_asc', label: 'По дате выхода <br> (сначала старые)'},
	{value: 'date_desc', label: 'По дате выхода <br> (сначала новые)'},
];

const CatalogSort = ({className = '', variant}) => {
	const dispatch = useDispatch();
	const {trackSearch} = useSelector((state) => state.catalog);
	const {setSort, sort} = useCatalog();
	const {setName, name} = useFilter();
	const [value, setValue] = useState(options[0]);

	const resetAllFilters = () => {
		setSort(undefined);
		
		if (name) {
			setName('');
		}
		
		if (trackSearch.length) {
			dispatch(setCatalogProperty({property: 'trackSearch', value: []}));
		}
	};

	const sortHandler = ({value}) => {
		const selectedOption = options.find(opt => opt.value === value);
		setValue(selectedOption);
		
		if (value === '') {
			resetAllFilters();
		} else {
			setSort(value);
			
			if (name) {
				setName('');
			}
			if (trackSearch.length) {
				dispatch(setCatalogProperty({property: 'trackSearch', value: []}));
			}
		}
	};

	useEffect(() => {
		if (!!sort) {
			const foundOption = options.find((option) => option.value === sort);
			if (foundOption) {
				setValue(foundOption);
			} else {
				setValue(options[0]);
			}
		} else {
			setValue(options[0]);
		}
	}, [sort]);

	const isMobile = variant === 'mobile';

	return (
		<StyledSort className={`select--sorting text--small Select ${className}`.trim()}>

			<StyledSelect
				$mobile={isMobile}
				options={options}
				onChange={sortHandler}
				defaultValue={value}
				classNamePrefix="react-select"
				components={{
					Control: isMobile ? MobileControl : Control,
					Option,
					SingleValue,
				}}
				value={value}
				isSearchable={!isMobile}
				menuPortalTarget={isMobile && typeof document !== 'undefined' ? document.body : undefined}
				styles={isMobile ? {menuPortal: (base) => ({...base, zIndex: 1200})} : undefined}
			/>

		</StyledSort>
	);
};

export default memo(CatalogSort);